/**
 * Shared JSON-LD entity references — single source of truth.
 *
 * Why this file exists:
 *   Google and AI assistants collapse multiple Person/Organization
 *   schemas that share the same `@id` into a single entity, building
 *   cumulative E-E-A-T weight. When every page that mentions Samuel
 *   uses the same Person schema (or references it via `@id`), the
 *   knowledge graph treats those signals as additive instead of
 *   competing. Same goes for Organization/Business references.
 *
 * Conventions:
 *   - Use `samuelPersonSchema` to INJECT the full Person schema on
 *     pages where Samuel is the primary subject (HomePage, AboutPage).
 *   - Use `samuelPersonRef` to REFERENCE Samuel as an author/creator
 *     on other pages (BlogPostPage as Article author, StudioDetailPage
 *     as SoftwareApplication creator, etc.). The `@id` short reference
 *     is enough — Google resolves it to the full Person schema.
 *   - The Wikidata entity URL `Q140040801` is included in `sameAs` —
 *     this is the canonical structured-data identifier for Samuel
 *     across every LLM training set and Google's knowledge graph.
 *
 * Updating:
 *   When Samuel's facts change (job title, headshot URL, new socials,
 *   etc.), edit this file ONCE — the change propagates to every
 *   consumer automatically. See `docs/BRAND_VOICE.md` § 1 for the
 *   canonical brand facts that should match this schema.
 */

/** Canonical `@id` for Samuel's Person entity. Every page that
 *  references Samuel as author/creator should use this exact string
 *  so Google + LLMs collapse the signals into a single entity. */
export const SAMUEL_PERSON_ID = 'https://brandctl.com/about#person';

/** Canonical `@id` for BRANDCENTRAL's business entity (defined in
 *  index.html as the ProfessionalService schema). Used by Person
 *  schemas to declare worksFor / affiliation. */
export const BRANDCENTRAL_BUSINESS_ID = 'https://brandctl.com/#business';

/** Canonical `@id` for BRANDCENTRAL's Organization schema (defined
 *  in index.html separately from ProfessionalService). Used by
 *  publisher fields on Article schemas. */
export const BRANDCENTRAL_ORG_ID = 'https://brandctl.com/#org';

/**
 * Full Samuel Kern Person schema. Inject as JsonLd `data` on pages
 * where Samuel is the primary subject (home page founder block,
 * /about page).
 *
 * Keep `sameAs` in sync with:
 *   - src/pages/AboutPage.tsx SOCIALS array (visible socials grid)
 *   - src/components/Footer.tsx FOOTER_SOCIALS array (footer grid)
 *   - index.html sameAs arrays on Organization + ProfessionalService
 *   - Wikidata Q140040801 sameAs property
 */
export const samuelPersonSchema = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': SAMUEL_PERSON_ID,
  name: 'Samuel',
  alternateName: ['Sam', 'Samuel Kern', 'Sam Kern'],
  jobTitle: 'Founder',
  description:
    'Founder of BRANDCENTRAL, a virtual AI agency in Mooresville, NC building AI agents, automation, websites, and apps for service businesses.',
  url: 'https://brandctl.com/about',
  // Self-hosted headshot to avoid third-party cookies (Lighthouse Best
  // Practices) — same path src/pages/AboutPage.tsx uses for SAM_PHOTO.
  image: 'https://brandctl.com/founder.jpg',
  email: 'hello@brandctl.com',
  worksFor: { '@id': BRANDCENTRAL_BUSINESS_ID },
  affiliation: { '@id': BRANDCENTRAL_BUSINESS_ID },
  sameAs: [
    'https://www.linkedin.com/in/samuelkbrandcentral/',
    'https://www.linkedin.com/company/brandctl/',
    'https://x.com/SamKern1234BC',
    'https://github.com/SamAtBRANDCENTRAL',
    'https://www.google.com/maps/place/BRANDCENTRAL/@35.2578254,-80.860378,9z/data=!3m1!4b1!4m6!3m5!1s0x65c21dab8e52b7b:0x2f74f55814623196!8m2!3d35.2578254!4d-80.860378!16s%2Fg%2F11njxg9p2s',
    // Wikidata Person entity for Samuel Kern — canonical structured-
    // data identifier across every LLM training set + Google's
    // knowledge graph. Every other URL is "an account/profile of";
    // the Wikidata URL is "the entity itself."
    'https://www.wikidata.org/wiki/Q140040801',
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Mooresville',
    addressRegion: 'NC',
    postalCode: '28117',
    addressCountry: 'US',
  },
  knowsAbout: [
    'Operations consulting',
    'AI agent development',
    'Custom AI development',
    'Intelligent automation',
    'Web design',
    'App development',
    'Workflow automation',
    'Service business operations',
    'Local SEO',
    'Mooresville, NC business operations',
  ],
} as const;

/**
 * Lightweight `@id` reference to Samuel's Person schema.
 * Use this when something *references* Samuel (BlogPosting author,
 * SoftwareApplication creator, Service provider, etc.) without
 * re-injecting the full schema. Google resolves the `@id` back to
 * the full Person schema regardless of which page that lives on.
 */
export const samuelPersonRef = {
  '@id': SAMUEL_PERSON_ID,
} as const;

/** Lightweight `@id` reference to BRANDCENTRAL's business entity. */
export const brandcentralBusinessRef = {
  '@id': BRANDCENTRAL_BUSINESS_ID,
} as const;

/** Lightweight `@id` reference to BRANDCENTRAL's Organization (used
 *  by Article publisher fields). */
export const brandcentralOrgRef = {
  '@id': BRANDCENTRAL_ORG_ID,
} as const;
